import { createApi } from '@reduxjs/toolkit/query/react'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import {
  CompleteOffboardingRecordRequest,
  CreateOffboardingChecklistRequest,
  DiscardOffboardingRecordRequest,
  OffboardingRecord,
  OffboardingRecordRequest,
  TriggerOffboardingRequest,
} from '@/features/offboarding/offboarding.def.ts'

export const offboardingApi = createApi({
  reducerPath: 'offboardingApi',
  tagTypes: ['Offboarding'],
  baseQuery: hcPortalBaseQuery(),
  endpoints: builder => ({
    triggerOffboarding: builder.mutation<number, TriggerOffboardingRequest>({
      query: (request) => ({
        url: '/Offboarding/TriggerOffboarding',
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: () => {
        return [{ type: 'Offboarding', id: 'LIST' }]
      },
    }),
    getOffboardingList: builder.query<OffboardingRecord[], void>({
      query: () => ({
        url: '/Offboarding/GetOffboardingRecordsList',
        method: 'GET',
        cache: 'no-cache',
      }),
      providesTags: (result) =>
        result
          ? [
            { type: 'Offboarding', id: 'LIST' },
            ...result.map(({ id }) => ({ type: 'Offboarding' as const, id })),
          ]
          : [{ type: 'Offboarding', id: 'LIST' }],
    }),
    getOffboardingRecord: builder.query<OffboardingRecord, OffboardingRecordRequest>({
      query: (request) => ({
        url: '/Offboarding/GetOffboardingRecord',
        method: 'GET',
        cache: 'no-cache',
        params: request,
      }),
      providesTags: (_result, _error, request) => [
        { type: 'Offboarding', id: request.offboardingRecordId },
      ],
    }),
    createOffboardingChecklist: builder.mutation<boolean, CreateOffboardingChecklistRequest>({
      query: (request) => ({
        url: `/Offboarding/CreateOffboardingChecklist`,
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'Offboarding', id: request.offboardingRecordId },
      ],
    }),
    discardOffboardingRecord: builder.mutation<boolean, DiscardOffboardingRecordRequest>({
      query: (request) => ({
        url: `/Offboarding/DiscardOffboardingRecord`,
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'Offboarding', id: request.offboardingRecordId },
      ],
    }),
    completeOffboardingRecord: builder.mutation<boolean, CompleteOffboardingRecordRequest>({
      query: (request) => ({
        url: `/Offboarding/CompleteOffboardingRecord`,
        method: 'POST',
        cache: 'no-cache',
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: 'Offboarding', id: request.offboardingRecordId },
      ],
    }),
  }),
})

export const {
  useTriggerOffboardingMutation,
  useGetOffboardingListQuery,
  useGetOffboardingRecordQuery,
  useCreateOffboardingChecklistMutation,
  useDiscardOffboardingRecordMutation,
  useCompleteOffboardingRecordMutation,
} = offboardingApi